import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, Send, X, AlertTriangle, Lightbulb, Loader2 } from 'lucide-react'
import { useDataPilot } from './DataPilotProvider'
import type { DPAnomaly } from '../../api/datapilot'

const SEVERITY_STYLES: Record<DPAnomaly['severity'], string> = {
  info: 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/50 dark:bg-sky-900/20 dark:text-sky-300',
  warning: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300',
  critical: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300',
}

/** Floating launcher button — always visible inside the protected layout. */
export const DataPilotLauncher: React.FC = () => {
  const { t } = useTranslation()
  const { open, openChat } = useDataPilot()
  if (open) return null
  return (
    <button
      onClick={openChat}
      aria-label={t('datapilot.openChat', 'Ask DataPilot')}
      className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-accent px-5 py-3 font-semibold text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl"
    >
      <Sparkles className="h-5 w-5" />
      <span className="hidden sm:inline">{t('datapilot.title', 'DataPilot')}</span>
    </button>
  )
}

/** Sliding drawer with the conversation + per-metric context. */
export const DataPilotDrawer: React.FC = () => {
  const { t } = useTranslation()
  const { open, close, messages, activeMetric, ask, loading } = useDataPilot()
  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = scrollRef.current
    // scrollTo is unimplemented in jsdom; guard so tests don't throw.
    el?.scrollTo?.({ top: el.scrollHeight, behavior: 'smooth' })
  }, [messages])

  if (!open) return null

  const submit = (q: string) => {
    const text = q.trim()
    if (!text) return
    ask(text)
    setInput('')
  }

  return (
    <>
      {/* backdrop (mobile) */}
      <div className="fixed inset-0 z-40 bg-black/20 sm:hidden" onClick={close} />
      <aside className="fixed bottom-0 right-0 top-0 z-50 flex w-full flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:w-[420px]">
        {/* header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-accent/5 px-4 py-3 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-white">
              <Sparkles className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white">{t('datapilot.title', 'DataPilot')}</p>
              <p className="text-xs text-slate-500">{t('datapilot.subtitle', 'AI insights for every metric')}</p>
            </div>
          </div>
          <button onClick={close} aria-label={t('datapilot.close', 'Close')} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* metric context */}
        {activeMetric?.entry && (
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/50">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{activeMetric.entry.name}</p>
            {activeMetric.entry.formula && (
              <p className="mt-1 font-mono text-xs text-slate-500">{activeMetric.entry.formula}</p>
            )}
          </div>
        )}

        {/* messages */}
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {messages.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-700">
              {t('datapilot.emptyState', 'Ask anything about your KPIs — what a metric means, why it moved, or what to watch.')}
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id}>
              {m.role === 'user' ? (
                <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-accent px-3 py-2 text-sm text-white">
                  {m.content}
                </div>
              ) : (
                <div className="space-y-3">
                  {m.loading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('datapilot.loading', 'DataPilot is thinking…')}
                    </div>
                  ) : (
                    <>
                      <div className="w-fit max-w-[90%] rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-2 text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-100">
                        {m.content}
                        {m.source && (
                          <span className="ml-2 align-middle rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-500 dark:bg-slate-700">
                            {m.source === 'demo' ? t('datapilot.demoBadge', 'demo') : t('datapilot.liveBadge', 'AI')}
                          </span>
                        )}
                      </div>

                      {m.insights && m.insights.length > 0 && (
                        <ul className="space-y-1.5">
                          {m.insights.map((ins, i) => (
                            <li key={i} className="flex gap-2 text-sm text-slate-700 dark:text-slate-300">
                              <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                              <span>{ins}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {m.anomalies?.map((a, i) => (
                        <div key={i} className={`flex gap-2 rounded-lg border px-3 py-2 text-xs ${SEVERITY_STYLES[a.severity]}`}>
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                          <span>{a.message}</span>
                        </div>
                      ))}

                      {m.suggested && m.suggested.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          {m.suggested.map((s, i) => (
                            <button
                              key={i}
                              onClick={() => submit(s)}
                              className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600 transition-colors hover:border-accent hover:text-accent dark:border-slate-600 dark:text-slate-300"
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* input */}
        <div className="border-t border-slate-200 p-3 dark:border-slate-700">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              submit(input)
            }}
            className="flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('datapilot.input', 'Ask about your data…')}
              className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-accent dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              aria-label={t('datapilot.send', 'Send')}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-white transition-opacity disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </aside>
    </>
  )
}

/** Mounts both the launcher and the drawer. Place once inside the layout. */
export const DataPilot: React.FC = () => (
  <>
    <DataPilotLauncher />
    <DataPilotDrawer />
  </>
)
